//File imports
import HttpService from '../../../services/Http/HttpService';
import Logger from '../../../services/Logger/Logger';
import Validator from '../../../services/Validator/Validator';
import {
    API_CREATE_DOCUMENT_TEMPLATE_TRANSLATION,
    API_DOCUMENT_TEMPLATE_TRANSLATION_IMAGE_UPLOAD,
    API_IS_OTHER_TRANSLATION_EXISTS,
    API_IS_URL_EXISTS,
    API_UPDATE_DOCUMENT_TEMPLATE_TRANSLATION,
    API_UPDATE_DOCUMENT_TEMPLATE_TRANSLATION_STATUS,
    GET_SINGLE_DOCUMENT_TEMPLATE_TRANSLATION,
} from './DocumentTemplateTranslationConstants';
import DocumentTemplateTranslationResultsPojo from './DocumentTemplateTranslationResultsPojo';
import OtherTranslationExistsResultsPojo from './OtherTranslationExistsResultsPojo';

export class DocumentTemplateTranslationService {
    private httpService = new HttpService();
    private logger = new Logger();
    private validator = new Validator();

    async create(data: any): Promise<{ success: boolean; message: string }> {
        const {
            documentTitle,
            url,
            metaTitle,
            metaKeyword,
            metaDescription,
            youtubeLink,
            documentText,
            shortDescription,
            description,
            faq,
            documentTemplateId,
            languageId,
            folderName,
            sourceDocumentTemplateTranslationId,
            previewBlurredImages,
        } = data;
        try {
            this.validator.isValidString('title', documentTitle);
            this.validator.isValidString('url', url);
            this.validator.isValidString('metaTitle', metaTitle);
            this.validator.isValidString('metaKeyword', metaKeyword);
            this.validator.isValidString('metaDescription', metaDescription);
            this.validator.isValidString('shortDescription', shortDescription);
            this.validator.isValidString('description', description);
            if (youtubeLink) {
                this.validator.isValidString('youtubeVideoLink', youtubeLink);
            }
            if (
                sourceDocumentTemplateTranslationId === '' ||
                sourceDocumentTemplateTranslationId == null
            ) {
                this.validator.isValidString('documentText', documentText);
            }
            if (
                sourceDocumentTemplateTranslationId != '' &&
                sourceDocumentTemplateTranslationId != null
            ) {
                this.validator.validatePositiveInteger(
                    sourceDocumentTemplateTranslationId,
                );
            }

            let formData = new FormData();
            formData.append('documentTemplateId', documentTemplateId);
            formData.append('languageId', languageId);
            formData.append('title', documentTitle);
            formData.append('url', url.toLowerCase());
            formData.append('metaTitle', metaTitle);
            formData.append('metaKeyword', metaKeyword);
            formData.append('metaDescription', metaDescription);
            formData.append('shortDescription', shortDescription);
            formData.append('description', description);
            formData.append('documentText', documentText);
            formData.append('faq', JSON.stringify(faq));
            formData.append('folderName', folderName);

            if (youtubeLink) {
                formData.append('youtubeVideoLink', youtubeLink);
            }

            if (
                sourceDocumentTemplateTranslationId != '' &&
                sourceDocumentTemplateTranslationId != null
            ) {
                formData.append(
                    'sourceDocumentTemplateTranslationId',
                    sourceDocumentTemplateTranslationId,
                );
            }

            if (previewBlurredImages) {
                formData.append(
                    'previewBlurredImages',
                    JSON.stringify(previewBlurredImages),
                );
            }

            const httpResult = await this.httpService.postFormData(
                API_CREATE_DOCUMENT_TEMPLATE_TRANSLATION,
                formData,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async update(data: any): Promise<{ success: boolean; message: string }> {
        const {
            documentTitle,
            url,
            metaTitle,
            metaKeyword,
            metaDescription,
            youtubeLink,
            documentText,
            shortDescription,
            description,
            faq,
            documentTemplateId,
            languageId,
            documentTemplateTranslationId,
            folderName,
            sourceDocumentTemplateTranslationId,
            previewBlurredImages,
        } = data;

        try {
            this.validator.validatePositiveInteger(documentTemplateId);
            this.validator.validatePositiveInteger(languageId);
            this.validator.validatePositiveInteger(
                documentTemplateTranslationId,
            );
            this.validator.isValidString('title', documentTitle);
            this.validator.isValidString('url', url);
            this.validator.isValidString('metaTitle', metaTitle);
            this.validator.isValidString('metaKeyword', metaKeyword);
            this.validator.isValidString('metaDescription', metaDescription);
            this.validator.isValidString('shortDescription', shortDescription);
            this.validator.isValidString('description', description);
            if (youtubeLink) {
                this.validator.isValidString('youtubeVideoLink', youtubeLink);
            }
            if (
                sourceDocumentTemplateTranslationId === '' ||
                sourceDocumentTemplateTranslationId == null
            ) {
                this.validator.isValidString('documentText', documentText);
            }
            if (
                sourceDocumentTemplateTranslationId != '' &&
                sourceDocumentTemplateTranslationId != null
            ) {
                this.validator.validatePositiveInteger(
                    sourceDocumentTemplateTranslationId,
                );
            }

            let formData = new FormData();
            formData.append('documentTemplateId', documentTemplateId);
            formData.append(
                'documentTemplateTranslationId',
                documentTemplateTranslationId,
            );
            formData.append('languageId', languageId);
            formData.append('title', documentTitle);
            formData.append('url', url.toLowerCase());
            formData.append('metaTitle', metaTitle);
            formData.append('metaKeyword', metaKeyword);
            formData.append('metaDescription', metaDescription);
            formData.append('shortDescription', shortDescription);
            formData.append('description', description);
            formData.append('documentText', documentText);
            formData.append('youtubeVideoLink', youtubeLink);
            formData.append('folderName', folderName);
            formData.append('faq', JSON.stringify(faq));
            if (previewBlurredImages != null) {
                formData.append(
                    'previewBlurredImages',
                    JSON.stringify(previewBlurredImages),
                );
            }

            if (
                sourceDocumentTemplateTranslationId != '' &&
                sourceDocumentTemplateTranslationId != null
            ) {
                formData.append(
                    'sourceDocumentTemplateTranslationId',
                    sourceDocumentTemplateTranslationId,
                );
            }

            const httpResult = await this.httpService.postFormData(
                API_UPDATE_DOCUMENT_TEMPLATE_TRANSLATION,
                formData,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async get(data: any): Promise<DocumentTemplateTranslationResultsPojo> {
        const { documentTemplateId, languageId } = data;
        try {
            this.validator.validatePositiveInteger(documentTemplateId);
            this.validator.validatePositiveInteger(languageId);

            const httpResult = await this.httpService.get(
                GET_SINGLE_DOCUMENT_TEMPLATE_TRANSLATION(
                    documentTemplateId,
                    languageId,
                ),
                {},
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
                data: httpResult.data.response,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async isOtherTranslationExists(
        data: any,
    ): Promise<OtherTranslationExistsResultsPojo> {
        const { documentTemplateId, languageId } = data;
        try {
            this.validator.validatePositiveInteger(documentTemplateId);
            this.validator.validatePositiveInteger(languageId);

            const httpResult = await this.httpService.get(
                API_IS_OTHER_TRANSLATION_EXISTS(documentTemplateId, languageId),
                {},
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
                data: httpResult.data.response,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async isUrlExists(
        url: string,
    ): Promise<{ success: boolean; message: string }> {
        try {
            this.validator.isValidString('url', url);
            const payload: any = {
                url: url.toLowerCase(),
            };

            const httpResult = await this.httpService.post(
                API_IS_URL_EXISTS,
                payload,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async delete(
        documentTemplateTranslationId: number,
        status: number,
    ): Promise<Boolean> {
        try {
            this.validator.validatePositiveInteger(
                documentTemplateTranslationId,
            );
            this.validator.validateIntegerIncludingZero(status);

            const httpResult = await this.httpService.delete(
                API_UPDATE_DOCUMENT_TEMPLATE_TRANSLATION_STATUS,
                { documentTemplateTranslationId, status },
            );
            return httpResult.data.isSuccess;
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async uploadImage(
        data: any,
    ): Promise<{ success: boolean; message: string; data: any }> {
        const { folderName, type, previewImage } = data;
        try {
            this.validator.validatePositiveInteger(type);

            let formData = new FormData();
            formData.append('folderName', folderName);
            formData.append('type', type);
            formData.append('image', previewImage);

            const httpResult = await this.httpService.postFormData(
                API_DOCUMENT_TEMPLATE_TRANSLATION_IMAGE_UPLOAD,
                formData,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
                data: httpResult.data.response,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }
}
